import React, { useState, useEffect } from "react";
import {
    Box,
    Container,
    Typography,
    Button,
    Grid,
    Card,
    CardContent,
    useTheme,
    useMediaQuery,
    alpha,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Stepper,
    Step,
    StepLabel,
} from "@mui/material";
import {useNavigate} from "react-router-dom";
import { motion } from "framer-motion";
import SEO from '../components/SEO';
import SavingsIcon from "@mui/icons-material/Savings";
import PeopleIcon from "@mui/icons-material/People";
import DescriptionIcon from "@mui/icons-material/Description";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupIcon from "@mui/icons-material/Group";
import EmailIcon from "@mui/icons-material/Email";
import PaymentIcon from "@mui/icons-material/Payment";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ClassIcon from "@mui/icons-material/Class";
import TimelineIcon from "@mui/icons-material/Timeline";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import {getAllStatistics} from "../api/statisticsApi";

const colors = {
    primary: "#0072E5",
    secondary: "#333333",
    accent: "#FF5722",
    lightGray: "#F8F9FA",
    darkGray: "#212529",
    white: "#FFFFFF",
    statsBg: "rgba(0, 114, 229, 0.05)"
};

const iOSImages = [
    "/pic/IOSimage0.png",
    "/pic/IOSimage1.png",
    "/pic/IOSimage2.png",
    "/pic/IOSimage3.png",
];

const androidImages = [
    "/pic/android-1.jpg",
    "/pic/android-2.jpg",
    "/pic/android-3.jpg",
    "/pic/android-4.jpg",
];

// iOS Install Modal
const IOSInstallModal = ({open, handleClose}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            label: "Step 1: Open menu (three dots)",
            description: "Open irontrack.ee on your iPhone with Safari browser, then tap the three-dot menu (...) in the bottom-right corner.",
            image: iOSImages[0],
        },
        {
            label: "Step 2: Tap Share",
            description: "In the menu that opens, tap 'Share'.",
            image: iOSImages[1],
        },
        {
            label: "Step 3: Choose Add to Home Screen",
            description: "In the share sheet, scroll down and tap 'Add to Home Screen'.",
            image: iOSImages[2],
        },
        {
            label: "Step 4: Confirm with Add",
            description: "Review the app name if needed, keep 'Open as Web App' enabled, then tap 'Add' in the top-right corner.",
            image: iOSImages[3],
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{sx: {borderRadius: 2, overflow: 'hidden'}}}
            fullScreen={isMobile}
        >
            <DialogTitle sx={{
                bgcolor: colors.primary,
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
                py: 2
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <AppleIcon/>
                    <Typography variant="h6">Install IronTrack on iOS</Typography>
                </Box>
                <IconButton edge="end" color="inherit" onClick={handleClose}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{p: 3, mt: 2}}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{mb: 4}}>
                    {steps.map((step) => (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{mt: 2, textAlign: 'center'}}>
                    <Typography variant="h6" gutterBottom>{steps[activeStep].label}</Typography>
                    <Typography variant="body1" paragraph sx={{mb: 3}}>
                        {steps[activeStep].description}
                    </Typography>
                    <Box
                        component="img"
                        src={steps[activeStep].image}
                        alt={`iOS installation step ${activeStep + 1}`}
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '550px',
                            border: '1px solid #eee',
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            mb: 2
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2, justifyContent: 'space-between'}}>
                <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    variant="outlined"
                >
                    Back
                </Button>
                <Box>
                    {activeStep === steps.length - 1 ? (
                        <Button onClick={handleClose} variant="contained" color="primary">
                            Done
                        </Button>
                    ) : (
                        <Button onClick={() => setActiveStep(activeStep + 1)} variant="contained" color="primary">
                            Next
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

// Android Install Modal
const AndroidInstallModal = ({open, handleClose}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [activeStep, setActiveStep] = useState(0);

    const steps = [
        {
            label: "Read all 4 steps first! - Open Chrome",
            description: "This feature works best in Chrome browser on Android. Open your website in Chrome.",
            image: androidImages[0],
        },
        {
            label: "Tap Menu Button",
            description: "Tap the menu button (three dots) in the top right corner of your browser.",
            image: androidImages[1],
        },
        {
            label: "Add to Home Screen",
            description: "Scroll down and tap 'Add to Home screen' from the menu options.",
            image: androidImages[2],
        },
        {
            label: "Confirm Installation",
            description: "Tap 'Add' in the confirmation dialog to install the app on your home screen.",
            image: androidImages[3],
        }
    ];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{sx: {borderRadius: 2, overflow: 'hidden'}}}
            fullScreen={isMobile}
        >
            <DialogTitle sx={{
                bgcolor: '#3ddc84',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
                py: 2
            }}>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                    <AndroidIcon/>
                    <Typography variant="h6">Install IronTrack on Android</Typography>
                </Box>
                <IconButton edge="end" color="inherit" onClick={handleClose}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{p: 3, mt: 2}}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{mb: 4}}>
                    {steps.map((step) => (
                        <Step key={step.label}>
                            <StepLabel>{step.label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Box sx={{mt: 2, textAlign: 'center'}}>
                    <Typography variant="h6" gutterBottom>{steps[activeStep].label}</Typography>
                    <Typography variant="body1" paragraph sx={{mb: 3}}>
                        {steps[activeStep].description}
                    </Typography>
                    <Box
                        component="img"
                        src={steps[activeStep].image}
                        alt={`Android installation step ${activeStep + 1}`}
                        sx={{
                            maxWidth: '100%',
                            height: 'auto',
                            maxHeight: '550px',
                            border: '1px solid #eee',
                            borderRadius: 2,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            mb: 2
                        }}
                    />
                </Box>
            </DialogContent>
            <DialogActions sx={{px: 3, py: 2, justifyContent: 'space-between'}}>
                <Button
                    disabled={activeStep === 0}
                    onClick={() => setActiveStep(activeStep - 1)}
                    variant="outlined"
                >
                    Back
                </Button>
                <Box>
                    {activeStep === steps.length - 1 ? (
                        <Button
                            onClick={handleClose}
                            variant="contained"
                            sx={{ bgcolor: '#3ddc84', '&:hover': { bgcolor: '#2bb56a' } }}
                        >
                            Done
                        </Button>
                    ) : (
                        <Button
                            onClick={() => setActiveStep(activeStep + 1)}
                            variant="contained"
                            sx={{ bgcolor: '#3ddc84', '&:hover': { bgcolor: '#2bb56a' } }}
                        >
                            Next
                        </Button>
                    )}
                </Box>
            </DialogActions>
        </Dialog>
    );
};

// StatCounter Component
const StatCounter = React.memo(({ value, label, icon, delay = 0.2 }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [count, setCount] = useState(0);

    useEffect(() => {
        const duration = 2500;
        const steps = 60;
        const increment = value / steps;
        let current = 0;

        const timer = setInterval(() => {
            current += increment;
            if (current >= value) {
                setCount(value);
                clearInterval(timer);
            } else {
                setCount(Math.floor(current));
            }
        }, duration / steps);

        return () => clearInterval(timer);
    }, [value]);

    return (
        <Card
            component={motion.div}
            initial={{y: 20, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            transition={{delay, duration: 0.5}}
            elevation={2}
            sx={{
                borderRadius: {xs: 3, md: 4},
                overflow: 'hidden',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 10px 30px rgba(0,114,229,0.2)',
                }
            }}
        >
            <CardContent sx={{p: {xs: 2.5, md: 3}, textAlign: 'center'}}>
                <Box sx={{mb: {xs: 1.5, md: 2}, display: 'flex', justifyContent: 'center'}}>
                    <Box sx={{
                        width: {xs: 50, md: 60},
                        height: {xs: 50, md: 60},
                        borderRadius: '50%',
                        bgcolor: alpha(colors.primary, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {React.cloneElement(icon, {sx: {fontSize: {xs: 24, md: 30}}})}
                    </Box>
                </Box>
                <Typography
                    variant={isMobile ? "h3" : "h2"}
                    component="div"
                    sx={{
                        fontWeight: 700,
                        color: colors.primary,
                        mb: 0.5,
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'}
                    }}
                >
                    {count.toLocaleString()}
                </Typography>
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontSize: {xs: '0.75rem', md: '0.875rem'}
                    }}
                >
                    {label}
                </Typography>
            </CardContent>
        </Card>
    );
});

// FeatureCard Component
const FeatureCard = React.memo(({ icon, title, description, delay = 0.3 }) => {
    return (
        <Card
            component={motion.div}
            initial={{y: 30, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            transition={{delay, duration: 0.5}}
            elevation={2}
            sx={{
                borderRadius: {xs: 3, md: 4},
                overflow: 'hidden',
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
                    '& .icon-container': {
                        bgcolor: colors.primary,
                        '& svg': {
                            color: colors.white,
                        }
                    }
                }
            }}
        >
            <CardContent sx={{p: {xs: 2.5, md: 3}}}>
                <Box
                    className="icon-container"
                    sx={{
                        width: {xs: 60, md: 70},
                        height: {xs: 60, md: 70},
                        borderRadius: "50%",
                        bgcolor: alpha(colors.primary, 0.1),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        transition: 'all 0.3s ease',
                    }}
                >
                    {React.cloneElement(icon, {
                        sx: {
                            fontSize: {xs: 28, md: 32},
                            color: colors.primary,
                            transition: 'all 0.3s ease',
                        }
                    })}
                </Box>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: "bold",
                        mb: 1.5,
                        fontFamily: '"Montserrat", sans-serif',
                        fontSize: {xs: '1.1rem', md: '1.25rem'}
                    }}
                >
                    {title}
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                        lineHeight: 1.6,
                        fontSize: {xs: '0.875rem', md: '1rem'}
                    }}
                >
                    {description}
                </Typography>
            </CardContent>
        </Card>
    );
});

// SectionTitle Component
const SectionTitle = ({title, subtitle, align = "center", delay = 0.2}) => {
    return (
        <Box
            component={motion.div}
            initial={{y: 20, opacity: 0}}
            animate={{y: 0, opacity: 1}}
            transition={{delay, duration: 0.5}}
            sx={{mb: {xs: 4, md: 5}, textAlign: align, position: 'relative', px: {xs: 2, sm: 0}}}
        >
            <Typography
                variant="h4"
                sx={{
                    fontWeight: "bold",
                    mb: 1.5,
                    fontFamily: '"Montserrat", sans-serif',
                    position: 'relative',
                    display: align === "center" ? 'inline-block' : 'block',
                    fontSize: {xs: '1.75rem', sm: '2rem', md: '2.125rem'}
                }}
            >
                {title}
                <Box sx={{
                    position: 'absolute',
                    height: 4,
                    width: 60,
                    backgroundColor: colors.accent,
                    bottom: -10,
                    left: align === "center" ? '50%' : 0,
                    transform: align === "center" ? 'translateX(-50%)' : 'none',
                    borderRadius: 2
                }}/>
            </Typography>
            {subtitle && (
                <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{
                        maxWidth: align === "center" ? 700 : 'unset',
                        mx: align === "center" ? "auto" : 0,
                        mt: 3,
                        lineHeight: 1.6,
                        fontSize: {xs: '0.95rem', md: '1rem'}
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

// Main Component
export default function HomePage() {
    const [stats, setStats] = useState({users: 0, trainings: 0, records: 0});
    const [loading, setLoading] = useState(true);
    const [iOSModalOpen, setIOSModalOpen] = useState(false);
    const [androidModalOpen, setAndroidModalOpen] = useState(false);
    const [androidInstallPromptEvent, setAndroidInstallPromptEvent] = useState(null);
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchStatistics = async () => {
            try {
                const data = await getAllStatistics();
                setStats({
                    users: data.users || 0,
                    trainings: data.trainings || 0,
                    records: data.records || 0
                });
                setLoading(false);
            } catch (error) {
                console.error("Failed to fetch statistics:", error);
                setLoading(false);
            }
        };
        fetchStatistics();
    }, []);

    useEffect(() => {
        const handleBeforeInstallPrompt = (event) => {
            event.preventDefault();
            setAndroidInstallPromptEvent(event);
        };

        const handleAppInstalled = () => {
            setAndroidInstallPromptEvent(null);
        };

        window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
        window.addEventListener("appinstalled", handleAppInstalled);

        return () => {
            window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
            window.removeEventListener("appinstalled", handleAppInstalled);
        };
    }, []);

    const isAndroidChromeOrEdge = () => {
        const userAgent = navigator.userAgent || "";
        const isAndroid = /Android/i.test(userAgent);
        const isEdge = /EdgA?\//i.test(userAgent);
        const isChrome =
            /Chrome\//i.test(userAgent) &&
            !/EdgA?\//i.test(userAgent) &&
            !/OPR\//i.test(userAgent) &&
            !/SamsungBrowser\//i.test(userAgent);

        return isAndroid && (isEdge || isChrome);
    };

    const handleAndroidInstall = async () => {
        // Only Chrome/Edge on Android gets native prompt. Others see manual steps.
        if (!isAndroidChromeOrEdge()) {
            setAndroidModalOpen(true);
            return;
        }

        if (!androidInstallPromptEvent) {
            setAndroidModalOpen(true);
            return;
        }

        androidInstallPromptEvent.prompt();
        await androidInstallPromptEvent.userChoice;
        setAndroidInstallPromptEvent(null);
    };

    const AppButton = ({icon, label, bgColor, onClick}) => (
        <Button
            variant="contained"
            startIcon={icon}
            onClick={onClick}
            fullWidth={isMobile}
            sx={{
                py: {xs: 1.5, md: 1.2},
                px: {xs: 3, md: 3},
                fontSize: {xs: "0.95rem", md: "0.9rem"},
                fontWeight: 600,
                borderRadius: 2,
                bgcolor: bgColor,
                textTransform: "none",
                boxShadow: `0 4px 10px ${alpha(bgColor, 0.3)}`,
                transition: "all 0.3s ease",
                "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 15px ${alpha(bgColor, 0.5)}`,
                    bgcolor: alpha(bgColor, 0.9)
                }
            }}
        >
            {label}
        </Button>
    );

    return (
        <Box sx={{overflowX: 'hidden'}}>
            <IOSInstallModal open={iOSModalOpen} handleClose={() => setIOSModalOpen(false)} />
            <AndroidInstallModal open={androidModalOpen} handleClose={() => setAndroidModalOpen(false)} />

            {/* HERO SECTION */}
            <Box sx={{
                minHeight: {xs: "auto", md: "80vh"},
                background: 'white',
                display: "flex",
                alignItems: "center",
                position: "relative",
                overflow: "hidden",
                py: {xs: 6, md: 0},
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `radial-gradient(circle at 20% 50%, ${alpha(colors.primary, 0.05)} 0%, transparent 50%),
                                radial-gradient(circle at 80% 80%, ${alpha(colors.accent, 0.03)} 0%, transparent 50%)`,
                    zIndex: 0
                }
            }}>
                <Container maxWidth="lg" sx={{position: 'relative', zIndex: 2}}>
                    <Grid container spacing={{xs: 4, md: 4}} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                animate={{x: 0, opacity: 1}}
                                transition={{duration: 0.8}}
                                sx={{px: {xs: 2, sm: 0}}}
                            >
                                <Typography
                                    variant="h2"
                                    sx={{
                                        fontWeight: 800,
                                        mb: {xs: 2, md: 3},
                                        color: colors.secondary,
                                        fontFamily: '"Montserrat", sans-serif',
                                        lineHeight: 1.2,
                                        fontSize: {xs: '2rem', sm: '2.5rem', md: '3rem'}
                                    }}
                                >
                                    Take Your CrossFit Box to the Next Level
                                </Typography>
                                <Typography
                                    variant="h6"
                                    sx={{
                                        mb: {xs: 3, md: 4},
                                        color: 'text.secondary',
                                        lineHeight: 1.7,
                                        fontWeight: 400,
                                        fontSize: {xs: '1rem', sm: '1.1rem', md: '1.25rem'}
                                    }}
                                >
                                    Specifically designed for CrossFit owners and coaches. Simplify member management,
                                    automate payments, and power your community.
                                </Typography>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, mb: 3}}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => navigate("/register")}
                                        endIcon={<ArrowForwardIcon />}
                                        fullWidth={isMobile}
                                        sx={{
                                            py: {xs: 1.8, md: 1.5},
                                            px: 4,
                                            fontSize: {xs: "1.05rem", md: "1rem"},
                                            fontWeight: 600,
                                            borderRadius: 2,
                                            textTransform: "none",
                                            bgcolor: colors.primary,
                                            color: 'white',
                                            boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                            "&:hover": {
                                                bgcolor: '#0052a3',
                                                transform: "translateY(-2px)",
                                                boxShadow: "0 6px 20px rgba(0,114,229,0.4)",
                                            }
                                        }}
                                    >
                                        Get Started Now
                                    </Button>
                                </Box>
                                <Box sx={{display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2}}>
                                    <AppButton
                                        icon={<AppleIcon/>}
                                        label="Install on iOS"
                                        bgColor="#000000"
                                        onClick={() => setIOSModalOpen(true)}
                                    />
                                    <AppButton
                                        icon={<AndroidIcon/>}
                                        label="Install on Android"
                                        bgColor="#3ddc84"
                                        onClick={handleAndroidInstall}
                                    />
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6} sx={{display: {xs: 'none', md: 'block'}}}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                animate={{x: 0, opacity: 1}}
                                transition={{duration: 0.8, delay: 0.2}}
                                sx={{
                                    borderRadius: 4,
                                    overflow: 'hidden',
                                    boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                                    position: 'relative',
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, transparent 100%)`,
                                        pointerEvents: 'none'
                                    }
                                }}
                            >
                                <img
                                    src="/pic/blog-1.jpg"
                                    alt="CrossFit Training"
                                    style={{width: '100%', height: 'auto', display: 'block'}}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* MISSION SECTION WITH IMAGE */}
            <Container sx={{py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <Grid container spacing={{xs: 4, md: 6}} alignItems="center">
                    <Grid item xs={12} md={6} sx={{display: {xs: 'none', md: 'block'}}}>
                        <Box
                            component={motion.div}
                            initial={{x: -50, opacity: 0}}
                            whileInView={{x: 0, opacity: 1}}
                            transition={{duration: 0.6}}
                            viewport={{once: true}}
                            sx={{borderRadius: {xs: 3, md: 4}, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                        >
                            <img src="/pic/blog-2.jpg" alt="Our Mission" style={{width: '100%', height: 'auto', display: 'block'}} />
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            component={motion.div}
                            initial={{x: 50, opacity: 0}}
                            whileInView={{x: 0, opacity: 1}}
                            transition={{duration: 0.6}}
                            viewport={{once: true}}
                        >
                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    mb: 3,
                                    fontFamily: '"Montserrat", sans-serif',
                                    position: 'relative',
                                    display: 'inline-block',
                                    fontSize: {xs: '1.75rem', sm: '2.25rem', md: '3rem'}
                                }}
                            >
                                Our Mission
                                <Box sx={{
                                    position: 'absolute',
                                    height: 4,
                                    width: 60,
                                    backgroundColor: colors.accent,
                                    bottom: -10,
                                    left: 0,
                                    borderRadius: 2
                                }}/>
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{
                                    lineHeight: 1.8,
                                    fontSize: {xs: '1rem', md: '1.1rem'}
                                }}
                            >
                                We're a team of CrossFit enthusiasts dedicated to building tools that simplify everyday
                                operations for gym owners and enhance the training experience for every athlete. From the
                                workout floor to the management back-office, we understand the challenges you face and
                                strive to solve them with innovative and user-friendly solutions.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            {/* STATISTICS SECTION */}
            <Box sx={{backgroundColor: colors.statsBg, py: {xs: 5, md: 8}, px: {xs: 2, sm: 3}}}>
                <Container maxWidth="lg">
                    <Typography
                        variant="h3"
                        sx={{
                            fontWeight: 700,
                            textAlign: 'center',
                            mb: {xs: 4, md: 5},
                            fontFamily: '"Montserrat", sans-serif',
                            position: 'relative',
                            display: 'inline-block',
                            width: '100%',
                            fontSize: {xs: '1.75rem', sm: '2.25rem', md: '3rem'}
                        }}
                    >
                        Our Impact in Numbers
                        <Box sx={{
                            position: 'absolute',
                            height: 4,
                            width: 60,
                            backgroundColor: colors.accent,
                            bottom: -12,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            borderRadius: 2
                        }}/>
                    </Typography>
                    <Grid container spacing={{xs: 2.5, md: 4}} justifyContent="center">
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCounter
                                value={stats.users}
                                label="Active Users"
                                icon={<PeopleIcon sx={{color: colors.primary}}/>}
                                delay={0.2}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCounter
                                value={stats.trainings}
                                label="Trainings"
                                icon={<FitnessCenterIcon sx={{color: colors.primary}}/>}
                                delay={0.4}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={4}>
                            <StatCounter
                                value={stats.records}
                                label="Records Broken"
                                icon={<EmojiEventsIcon sx={{color: colors.primary}}/>}
                                delay={0.6}
                            />
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* AFFILIATE OWNER FEATURES */}
            <Container sx={{py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <SectionTitle
                    title="Built for Affiliate Owners"
                    subtitle="Your passion is guiding your athletes to success, not juggling spreadsheets. We equip you with the tools you need to run your box smoothly."
                />
                <Grid container spacing={{xs: 2.5, md: 4}}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<AssignmentIcon/>}
                            title="Manage & Draft Contracts"
                            description="Easily create, edit, and organize membership contracts. Keep everything in one place without the fuss of endless paperwork."
                            delay={0.3}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<ClassIcon/>}
                            title="Add Classes & WODs"
                            description="Schedule your box's workouts, classes, and events with just a few clicks. Share the daily WOD and track attendance seamlessly."
                            delay={0.4}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<GroupIcon/>}
                            title="Member Management"
                            description="Add, remove, and organize members effortlessly. Keep them engaged with automated communications and updates."
                            delay={0.5}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<EmailIcon/>}
                            title="Send Bulk Emails"
                            description="Quickly inform your entire membership about schedule changes, new events, or box news. No more chaotic email chains."
                            delay={0.6}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<PaymentIcon/>}
                            title="Handle Payments"
                            description="Automate your billing and track payments. Lower transaction fees save you money so you can reinvest in your community."
                            delay={0.7}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<SavingsIcon/>}
                            title="Save on Transactions"
                            description="We offer lower payment processing fees compared to other platforms. Keep more of your revenue and invest in growing your gym."
                            delay={0.8}
                        />
                    </Grid>
                </Grid>
            </Container>

            {/* IMAGE SECTION WITH FEATURES SHOWCASE */}
            <Box sx={{bgcolor: colors.lightGray, py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <Container>
                    <Grid container spacing={{xs: 4, md: 4}} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{borderRadius: {xs: 3, md: 4}, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                            >
                                <img src="/pic/blog-3.jpg" alt="Community" style={{width: '100%', height: 'auto', display: 'block'}} />
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        mb: {xs: 2, md: 3},
                                        fontFamily: '"Montserrat", sans-serif',
                                        fontSize: {xs: '1.75rem', sm: '2.25rem', md: '3rem'}
                                    }}
                                >
                                    Build a Thriving Community
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.8,
                                        mb: {xs: 2, md: 3},
                                        fontSize: {xs: '1rem', md: '1.1rem'}
                                    }}
                                >
                                    Our platform helps you create stronger connections with your members. Track progress,
                                    celebrate achievements, and foster the supportive environment that makes CrossFit special.
                                </Typography>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 2}}>
                                    <Box sx={{display: 'flex', alignItems: 'start', gap: 2}}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            bgcolor: alpha(colors.primary, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <PeopleIcon sx={{color: colors.primary, fontSize: 20}} />
                                        </Box>
                                        <Typography variant="body1" color="text.secondary" sx={{fontSize: {xs: '0.95rem', md: '1rem'}}}>
                                            Engage members with automated updates and personalized communications
                                        </Typography>
                                    </Box>
                                    <Box sx={{display: 'flex', alignItems: 'start', gap: 2}}>
                                        <Box sx={{
                                            width: 40,
                                            height: 40,
                                            borderRadius: '50%',
                                            bgcolor: alpha(colors.primary, 0.1),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}>
                                            <TimelineIcon sx={{color: colors.primary, fontSize: 20}} />
                                        </Box>
                                        <Typography variant="body1" color="text.secondary" sx={{fontSize: {xs: '0.95rem', md: '1rem'}}}>
                                            Track member progress and celebrate milestones together
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* ATHLETE FEATURES */}
            <Container sx={{py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <SectionTitle
                    title="Perfect for Athletes & Regular Users"
                    subtitle="Our platform isn't just for owners—every athlete can get the most out of their membership, track progress, and sign up for classes with ease."
                />
                <Grid container spacing={{xs: 2.5, md: 4}}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<PaymentIcon/>}
                            title="Purchase Affiliate Plans"
                            description="Browse and sign up for the membership plan that suits your goals. Enjoy a transparent and secure checkout process."
                            delay={0.3}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<ClassIcon/>}
                            title="Register for Classes"
                            description="Book your spot in upcoming WODs or special events. Receive reminders, track your attendance, and never miss a session."
                            delay={0.4}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<TimelineIcon/>}
                            title="Track Your Progress"
                            description="Log your PRs and workout results. Monitor your progress over time and celebrate every victory, big or small."
                            delay={0.5}
                        />
                    </Grid>
                </Grid>
            </Container>

            {/* SINGLE IMAGE SECTION */}
            <Box sx={{bgcolor: colors.lightGray, py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <Container>
                    <Grid container spacing={4} justifyContent="center">
                        <Grid item xs={12} md={8}>
                            <Box
                                component={motion.div}
                                initial={{y: 50, opacity: 0}}
                                whileInView={{y: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{
                                    borderRadius: {xs: 3, md: 4},
                                    overflow: 'hidden',
                                    boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                                    height: {xs: '300px', sm: '400px', md: '500px'}
                                }}
                            >
                                <img src="/pic/blog-1.jpg" alt="CrossFit Community" style={{width: '100%', height: '100%', objectFit: 'cover'}} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* WHY CHOOSE US */}
            <Container sx={{py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <SectionTitle
                    title="Why Choose Our Platform?"
                    subtitle="Our comprehensive gym management solution helps CrossFit box owners save time, increase revenue, and deliver exceptional member experiences."
                />
                <Grid container spacing={{xs: 2.5, md: 4}}>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<PeopleIcon/>}
                            title="Manage Your Community"
                            description="Our platform is built to help affiliate owners run their business smoothly. Engage your members, track attendance, and ensure a strong community."
                            delay={0.3}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<SavingsIcon/>}
                            title="Save Money"
                            description="We offer lower payment processing fees compared to other platforms. Keep more of your revenue and invest in growing your gym."
                            delay={0.5}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                        <FeatureCard
                            icon={<DescriptionIcon/>}
                            title="Simple Contracts"
                            description="Easily create, manage, and track contracts for your members. Our system ensures compliance and simplifies document handling."
                            delay={0.7}
                        />
                    </Grid>
                </Grid>
            </Container>

            {/* FINAL IMAGE SECTION */}
            <Box sx={{py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <Container>
                    <Grid container spacing={{xs: 4, md: 4}} alignItems="center">
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: -50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                            >
                                <Typography
                                    variant="h3"
                                    sx={{
                                        fontWeight: 700,
                                        mb: {xs: 2, md: 3},
                                        fontFamily: '"Montserrat", sans-serif',
                                        fontSize: {xs: '1.75rem', sm: '2.25rem', md: '3rem'}
                                    }}
                                >
                                    Created by CrossFit Enthusiasts
                                </Typography>
                                <Typography
                                    variant="body1"
                                    color="text.secondary"
                                    sx={{
                                        lineHeight: 1.8,
                                        fontSize: {xs: '1rem', md: '1.1rem'}
                                    }}
                                >
                                    Built from the ground up to empower affiliate owners and members alike. We understand
                                    the unique challenges of running a CrossFit box because we've been there. Focus on
                                    what truly matters: forging elite fitness and building a thriving community.
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Box
                                component={motion.div}
                                initial={{x: 50, opacity: 0}}
                                whileInView={{x: 0, opacity: 1}}
                                transition={{duration: 0.6}}
                                viewport={{once: true}}
                                sx={{borderRadius: {xs: 3, md: 4}, overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.1)'}}
                            >
                                <img src="/pic/blog-2.jpg" alt="CrossFit Box" style={{width: '100%', height: 'auto', display: 'block'}} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            {/* CTA SECTION */}
            <Box sx={{backgroundColor: alpha(colors.primary, 0.03), py: {xs: 6, md: 10}, px: {xs: 2, sm: 3}}}>
                <Container maxWidth="md">
                    <Box
                        component={motion.div}
                        initial={{scale: 0.95, opacity: 0}}
                        whileInView={{scale: 1, opacity: 1}}
                        viewport={{once: true}}
                        transition={{duration: 0.5}}
                        sx={{
                            textAlign: "center",
                            p: {xs: 3, md: 6},
                            borderRadius: {xs: 3, md: 4},
                            backgroundColor: 'white',
                            boxShadow: '0 10px 40px rgba(0,0,0,0.05)',
                        }}
                    >
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 700,
                                mb: 2,
                                fontFamily: '"Montserrat", sans-serif',
                                fontSize: {xs: '1.75rem', sm: '2.25rem', md: '3rem'}
                            }}
                        >
                            Ready to Level Up?
                        </Typography>
                        <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{
                                mb: 4,
                                maxWidth: 600,
                                mx: 'auto',
                                fontSize: {xs: "1rem", md: "1.1rem"},
                                lineHeight: 1.6
                            }}
                        >
                            Whether you're an affiliate owner or an ambitious athlete, we have all the tools you need.
                            Join hundreds of gym owners who are streamlining their operations and creating better
                            experiences for their members.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => navigate("/register")}
                            endIcon={<ArrowForwardIcon />}
                            fullWidth={isMobile}
                            sx={{
                                py: {xs: 1.8, md: 1.5},
                                px: 5,
                                fontSize: {xs: "1.05rem", md: "1.1rem"},
                                fontWeight: 600,
                                borderRadius: 2,
                                textTransform: "none",
                                boxShadow: "0 4px 14px rgba(0,114,229,0.3)",
                                transition: "all 0.3s",
                                "&:hover": {
                                    transform: "translateY(-3px)",
                                    boxShadow: "0 8px 25px rgba(0,114,229,0.5)",
                                }
                            }}
                        >
                            Get Started Today
                        </Button>
                    </Box>
                </Container>
            </Box>

            {/* FOOTER */}
            <Box sx={{backgroundColor: colors.secondary, color: colors.white, py: {xs: 3, md: 4}, px: {xs: 2, sm: 3}}}>
                <Container>
                    <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5}}>
                        <Typography variant="body2" align="center" sx={{fontSize: {xs: '0.875rem', md: '1rem'}}}>
                            © {new Date().getFullYear()} Irontrack OÜ | All rights reserved.
                        </Typography>
                        <Typography variant="body2" align="center" sx={{opacity: 0.8, fontSize: {xs: '0.8rem', md: '0.875rem'}}}>
                            Reg. nr: 17184398 | Email: info@irontrack.ee
                        </Typography>
                        <Box sx={{mt: 1}}>
                            <a
                                href="/privacy-policy"
                                style={{color: 'white', textDecoration: 'none', fontSize: '0.875rem'}}
                            >
                                Privacy Policy
                            </a>
                        </Box>
                    </Box>
                </Container>
            </Box>
        </Box>
    );
}